/**
 * O contêiner do convite — onde a marca de quem convida é aplicada, e onde a
 * nossa não aparece.
 *
 * O tema vai NUM CONTÊINER, nunca no `:root`. Não é preferência de escrita: um
 * convite escuro precisa continuar escuro dentro de um navegador em modo claro,
 * e é a custom property declarada no próprio elemento que vence a herdada do
 * ancestral. `src/estilo/temas.css` foi adaptado exatamente para isso — as
 * regras valem em qualquer elemento, e não só no `<html>`.
 */

import type { CSSProperties, ReactNode } from 'react';

import { ACENTO_LIVRE, acentoEmUso, variaveisDoAcento } from '../../servicos/marca';
import type { Marca } from '../../tipos';

export type AtributosDaMarca = {
  readonly 'data-tema'?: string;
  readonly 'data-theme'?: 'light' | 'dark';
  readonly 'data-fonte'?: string;
  readonly 'data-acento'?: string;
};

const FONTES_CONHECIDAS = ['sistema', 'serifada', 'mono'];

/**
 * Traduz a marca para os três atributos que o CSS entende.
 *
 * `modo: 'auto'` não marca nada — é o terceiro estado da Régua, o único em que
 * `prefers-color-scheme` decide. Marcar `data-theme` ali travaria o convite num
 * modo que o organizador escolheu justamente não escolher.
 */
export function atributosDaMarca(marca: Marca | null): AtributosDaMarca {
  if (!marca) return {};

  const atributos: {
    'data-tema'?: string;
    'data-theme'?: 'light' | 'dark';
    'data-fonte'?: string;
    'data-acento'?: string;
  } = {};

  // Nome de tema é chave de uma lista fechada de 20. Sanear em vez de conferir
  // contra uma cópia da lista: manter os 20 nomes repetidos aqui é como um
  // tema novo da Régua nasce invisível para o convite.
  const tema = typeof marca.tema === 'string' ? marca.tema.trim().toLowerCase() : '';
  if (/^[a-z][a-z0-9-]{0,30}$/.test(tema)) atributos['data-tema'] = tema;

  if (marca.modo === 'claro') atributos['data-theme'] = 'light';
  if (marca.modo === 'escuro') atributos['data-theme'] = 'dark';

  if (FONTES_CONHECIDAS.includes(marca.fonte)) atributos['data-fonte'] = marca.fonte;

  // A cor livre não substitui `data-tema`: ela liga um bloco que sobrepõe as
  // seis variáveis do acento, deixando os neutros (que vêm de `[data-tema]`,
  // em `tokens.css`) intactos. Sem `data-tema` no contêiner, um convite escuro
  // perderia o fundo escuro e herdaria o do navegador de quem abriu.
  if (acentoEmUso(marca) !== null) atributos['data-acento'] = ACENTO_LIVRE;

  return atributos;
}

/**
 * As oito variáveis da cor livre, como estilo em linha.
 *
 * Estilo em linha é o único lugar onde um valor calculado em tempo de execução
 * pode entrar no CSS sem uma folha gerada — e é por isso que a cor derivada
 * chega por aqui e não por classe. Quem escolhe ENTRE os dois conjuntos (claro
 * e escuro) é o CSS, no bloco `[data-acento='livre']` de `temas.css`: estilo em
 * linha não sabe responder a `prefers-color-scheme`, e `modo: auto` significa
 * que os dois vão acontecer no mesmo elemento.
 *
 * `undefined` quando não há cor livre — e não um objeto vazio: um `style` vazio
 * ainda faz o React tocar no atributo a cada render.
 */
export function estiloDaMarca(marca: Marca | null): CSSProperties | undefined {
  if (!marca) return undefined;
  const hex = acentoEmUso(marca);
  if (hex === null) return undefined;
  const variaveis = variaveisDoAcento(hex);
  return variaveis === null ? undefined : (variaveis as CSSProperties);
}

export type PropsDaMoldura = {
  readonly marca: Marca | null;
  readonly children: ReactNode;
};

/** A casca temada. Não sabe o que mostra — só em que cor e em que fonte. */
export function Moldura({ marca, children }: PropsDaMoldura) {
  return (
    <div className="convite" {...atributosDaMarca(marca)} style={estiloDaMarca(marca)}>
      {marca?.capa_url ? (
        // Capa é decoração: o `alt` vazio a tira do caminho de quem usa leitor
        // de tela, que já vai ouvir o título logo abaixo.
        <img className="convite__capa" src={marca.capa_url} alt="" />
      ) : null}
      <div className="convite-coluna">{children}</div>
    </div>
  );
}

export type PropsDoCabecalho = {
  readonly titulo: string;
  readonly descricao: string;
  readonly marca: Marca | null;
  /**
   * Depois da primeira resposta o cabeçalho encolhe: o título continua ali —
   * a identidade é do organizador — mas para de empurrar a pergunta para
   * baixo da dobra no celular. Não é animação; é outro tamanho, sem transição.
   */
  readonly compacto: boolean;
};

export function Cabecalho({ titulo, descricao, marca, compacto }: PropsDoCabecalho) {
  const logo = marca?.logo_url ?? null;
  return (
    <header className={compacto ? 'cabecalho cabecalho--compacto' : 'cabecalho'}>
      {logo ? <img className="cabecalho__logo" src={logo} alt="" /> : null}
      <h1 className="cabecalho__titulo">{titulo}</h1>
      {descricao.trim() !== '' && !compacto ? (
        <p className="cabecalho__descricao">{descricao}</p>
      ) : null}
    </header>
  );
}
